import React, { useMemo } from 'react';
import { PERMISSIONS_MATRIX, ModuleKey, Role } from '../access/modules';
import { useCurrentUser } from '../hooks/useApi';
import { api } from '../lib/api';
import { useTenant } from './TenantContext';
import { TenantAccessContext } from './TenantAccessContext';

// Module keys that are capabilities: gated on the EFFECTIVE value (same as the
// backend PermissionsGuard) — e.g. Mesas/Cocina only with restaurant mode.
const CAPABILITY_MODULE_KEYS = ['inventory', 'crm', 'tables', 'kitchen', 'shopifyIntegration'] as const;
type CapabilityModuleKey = (typeof CAPABILITY_MODULE_KEYS)[number];
const isCapabilityModuleKey = (key: string): key is CapabilityModuleKey =>
    (CAPABILITY_MODULE_KEYS as readonly string[]).includes(key);

export const TenantAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { tenant, hasFeature, capabilities, isLoading: isTenantLoading } = useTenant();
    const { data: user, isLoading: isUserLoading, isError } = useCurrentUser();

    const effectiveUser = user || (isError ? api.getCurrentUser() : undefined);
    const role = effectiveUser?.role as Role | undefined;

    const access = useMemo(() => {
        const isOwner = role === 'OWNER';
        const isManager = role === 'MANAGER';
        const isCashier = role === 'CASHIER';
        const isSuperAdmin = effectiveUser?.isSuperAdmin === true;

        const can = (moduleKey: ModuleKey): boolean => {
            if (isSuperAdmin && tenant) return true;
            if (!tenant) return false;

            const config = PERMISSIONS_MATRIX[moduleKey];
            if (!config) return true;

            if (
                config.feature &&
                !(isCapabilityModuleKey(config.feature)
                    ? capabilities[config.feature]
                    : hasFeature(config.feature))
            ) {
                return false;
            }

            if (role && config.roles.includes(role)) {
                return true;
            }

            // User-specific permission grant overrides the role matrix
            // (e.g. CASHIER with POS_DISCOUNT granted individually in settings)
            const userPerms: string[] = (effectiveUser as any)?.permissions ?? [];
            if (userPerms.includes(moduleKey)) {
                return true;
            }

            return false;
        };

        return {
            can,
            role,
            isOwner,
            isManager,
            isCashier,
            user: effectiveUser,
            isLoading: isTenantLoading || isUserLoading,
        };
    }, [tenant, hasFeature, capabilities, role, effectiveUser, isTenantLoading, isUserLoading]);

    return (
        <TenantAccessContext.Provider value={access}>
            {children}
        </TenantAccessContext.Provider>
    );
};
